/*
  **Einführung** (#323, `docs/concept/gefuehrte-einfuehrung.md`, Variante G): was HappyTask neuen Nutzern
  zeigt. Zwölf Schritte in vier Bereichen; die Texte stehen unter `intro.steps.<id>` in den Locales.
  Reine Tabellen — Seitenhilfe, „Entdecken“ und die Lernaufgaben lesen alle hieraus.
*/

export const INTRO_STEPS = [
  'quickadd', 'quicknotes', 'clarify',
  'upcoming', 'dormant', 'recurring',
  'lifespaces', 'projects', 'board',
  'focus', 'done', 'reward',
] as const

export type IntroStepId = (typeof INTRO_STEPS)[number]

export type IntroArea = 'capture' | 'plan' | 'organize' | 'do'

export const STEP_AREA: Record<IntroStepId, IntroArea> = {
  quickadd: 'capture', quicknotes: 'capture', clarify: 'capture',
  upcoming: 'plan', dormant: 'plan', recurring: 'plan',
  lifespaces: 'organize', projects: 'organize', board: 'organize',
  focus: 'do', done: 'do', reward: 'do',
}

/** **Entdecken**: nur, was man in den Listen kaum sieht (Sven, 12.10.2026). */
export const DISCOVER_STEPS = ['quickadd', 'lifespaces', 'board', 'focus', 'reward'] as const satisfies readonly IntroStepId[]

export type DiscoverStepId = (typeof DISCOVER_STEPS)[number]

export function isDiscoverStep(step: IntroStepId): step is DiscoverStepId {
  return (DISCOVER_STEPS as readonly IntroStepId[]).includes(step)
}

/**
 * Lernaufgaben, die sich selbst abhaken: Ihre Handlung geschieht nicht an der Aufgabe selbst. Bei den
 * übrigen tut man es an ihr (Tag geben, schlafen legen, ▶, abhaken) — sie wandern dann von selbst.
 */
export const SELF_COMPLETING_STEPS: readonly IntroStepId[] = ['quickadd', 'quicknotes', 'lifespaces', 'projects', 'board']

/** Die Seiten mit **Seitenhilfe** (?-Button neben dem Titel). */
export type PageHelpId = 'quickNotes' | 'upcoming' | 'dormant' | 'recurring' | 'done' | 'project'

/** Was die Seitenhilfe einer Seite erklärt, in dieser Reihenfolge. */
export const PAGE_HELP_STEPS: Record<PageHelpId, readonly IntroStepId[]> = {
  quickNotes: ['quicknotes', 'quickadd', 'clarify'],
  upcoming: ['upcoming'],
  dormant: ['dormant'],
  recurring: ['recurring'],
  done: ['done', 'reward'],
  project: ['projects', 'board'],
}

/**
 * Wo „Entdecken“ einen Schritt zeigt: der Wert von `data-intro-anchor` am Ziel und die Seite, auf der die
 * Erklärung bevorzugt steht (Radix weicht aus, wenn dort kein Platz ist). Fokus und Belohnung zeigen an der
 * ersten Aufgabenzeile — ▶ und Rechtsklick gehören zu ihr.
 */
export const DISCOVER_ANCHOR: Record<DiscoverStepId, { anchor: string; side: 'top' | 'right' | 'bottom' | 'left' }> = {
  quickadd: { anchor: 'quickadd', side: 'bottom' },
  lifespaces: { anchor: 'lifespaces', side: 'right' },
  board: { anchor: 'board', side: 'left' },
  focus: { anchor: 'task', side: 'bottom' },
  reward: { anchor: 'task', side: 'bottom' },
}
